/**
 * Field Help — Tasks
 *
 * Keys MUST match the task field ids in
 * `features/tasks/components/config/fields/task-core-fields.ts` and
 * `task-status-schedule-fields.ts` (see TASK_CORE_FIELDS / TASK_STATUS_SCHEDULE_FIELDS)
 * so the "?" popover attaches to the right column / editor row / card field.
 *
 * Relationship context (used in `relatedTo`): a task is POLYMORPHIC — it links
 * to exactly one of a contact, company, ticket, deal, or communication via the
 * `entity_type` / `entity_id` discriminator pair. There is no `ticket_id` /
 * `deal_id` column on `contact_tasks`; ticket and deal linkage are expressed
 * through `entity_type` + `entity_id`. `contact_id` and `company_id` are the
 * only direct foreign keys. See `lib/actions/relationships/AGENTS.md`.
 */

import type { EntityFieldHelp } from "./types";

export const taskFieldHelp: EntityFieldHelp = {
  template: {
    title: "Template",
    description:
      "Pre-fills the task from a saved template — title, description, tags, priority, and status.",
    usage:
      "Pick a template to skip manual entry for recurring work (onboarding steps, Schooley Mitchell checklists, standard WTC engagements). It only seeds the form — edit any field after applying.",
  },
  title: {
    title: "Task Title",
    description: "Short statement of what needs to be done.",
    usage:
      "Keep it action-oriented and scannable (e.g. \"Call Acme re: renewal\"). It's the headline on the task list, card, and every reminder.",
  },
  description: {
    title: "Description",
    description: "The details of the work — context, steps, or acceptance criteria.",
    usage:
      "Add enough that whoever picks up the task can act without asking. Searchable, so include keywords you'd later look it up by.",
  },
  contact_id: {
    title: "Contact",
    description: "The person this task is about. One of the ways a task is linked to the CRM.",
    usage:
      "Set when the task concerns a specific person — a call to make, a follow-up to send. Optional if the task is linked to a company, ticket, or deal instead.",
    relatedTo:
      "Direct foreign key to contacts. A task links to exactly one entity (contact, company, ticket, deal, or communication) — see entity_type / entity_id for non-contact/company links.",
  },
  company_name: {
    title: "Company",
    description: "Display name of the linked company (read-only label, denormalized).",
    usage:
      "For scanning at a glance which account a task belongs to. Change the Company link to change this.",
    relatedTo: "Mirrors the linked Company record.",
  },
  company_id: {
    title: "Company",
    description:
      "The company this task belongs to. Used for account-level tasks that aren't tied to one specific person.",
    usage:
      "Set for work that spans an account rather than an individual. If left empty and no other entity is linked, generic internal tasks default to the West Tennessee Consulting operations company.",
    relatedTo:
      "Direct foreign key to companies. One of the task's linkable entities alongside contact, ticket, deal, and communication.",
  },
  entity_type: {
    title: "Linked Entity Type",
    description:
      "Which kind of record this task is attached to — contacts, companies, tickets, deals, or communications.",
    usage:
      "Read-only. Tasks are polymorphic: this is the discriminator that, with Entity ID, points the task at its parent record. Ticket and deal linkage live here — there is no separate ticket_id or deal_id column.",
    relatedTo:
      "Paired with Entity ID to resolve the linked record. Deal-linked tasks (entity_type \"deals\") surface in the Deal workspace tasks tab.",
  },
  entity_id: {
    title: "Linked Entity ID",
    description: "The UUID of the specific record this task is attached to.",
    usage:
      "Read-only. Combined with Entity Type to resolve the parent (the actual ticket, deal, or other record). Set automatically when you link the task to an entity.",
    relatedTo: "Resolved together with Entity Type. This is how ticket and deal linkage is stored.",
  },
  status: {
    title: "Status",
    description:
      "Where the task stands: pending, in_progress, completed, or cancelled.",
    usage:
      "Move it as work progresses. Open tasks (pending / in_progress) drive the overdue and pending views and follow-up reminders; completing or cancelling clears them from active lists.",
  },
  priority: {
    title: "Priority",
    description: "How urgent the task is: low, medium, high, or urgent.",
    usage:
      "Drives the high-priority view and sorting. Reserve high/urgent for work that genuinely needs to jump the queue so the signal stays meaningful.",
  },
  assigned_users: {
    title: "Assigned To",
    description: "The people responsible for the task (up to five).",
    usage:
      "Assign so the task shows up in each person's \"my tasks\" view and reminders. Leave unassigned only for an unclaimed backlog item.",
    relatedTo: "Drives who receives task reminders and which Slack DMs go out.",
  },
  due_date: {
    title: "Due Date",
    description: "When the task is due.",
    usage:
      "A task past its due date and not yet completed counts as overdue and surfaces in the overdue view and reminders. Keep it realistic so the overdue list stays trustworthy.",
  },
  tags: {
    title: "Tags",
    description: "Free-form labels for segmenting tasks (up to ten).",
    usage:
      "Use for your own grouping — campaign, checklist, vertical. Common WTC and Schooley Mitchell tag sets are offered as suggestions; custom tags are allowed.",
  },
  user_id: {
    title: "Creator",
    description: "The user who created the task (auto-populated).",
    usage:
      "Read-only. Used for the \"my tasks\" view, which includes tasks you created as well as tasks assigned to you.",
    relatedTo: "Foreign key to the app user who owns the record.",
  },
  created_at: {
    title: "Created",
    description: "When the task record was created.",
    usage: "Read-only. Default sort orders the list newest-first; useful for backlog aging.",
  },
};
